import { LIVE_MESSAGE_EVENT } from '../components/chat/events';

const CABLE_EVENT = 'output:internal:cable';
const NETWORK_EVENT = 'output:internal:network';

/** Recompute cadence while a recovery is outstanding; idle otherwise, so a healthy page never ticks. */
const LAG_TICK_MS = 1000;

/**
 * Whether the client is receiving data, which is not the same question as whether the socket is
 * open. A user can sit on a live cable with an unfetched gap behind them, or with a failed
 * reconciliation, and report themselves as online while missing messages.
 *
 * - `offline`    no network
 * - `connecting` network is up, cable is not
 * - `stale`      cable is up and a recovery *failed* — connected and knowingly behind
 * - `resuming`   cable is up and a recovery is outstanding
 * - `ready`      cable is up with nothing outstanding
 */
export type FreshnessPhase = 'offline' | 'connecting' | 'stale' | 'resuming' | 'ready';

/** Outstanding recovery work, sampled from whatever owns catchup (today: the envelope table). */
export interface RecoverySample {
  /** Recoveries known to be needed and not yet completed. */
  readonly pending: number;
  /** Recoveries that terminally failed; the client is behind and has stopped trying. */
  readonly failed: number;
  /** Start timestamp of the oldest outstanding recovery, or null when none is outstanding. */
  readonly oldestPendingSince: number | null;
}

const EMPTY_RECOVERY: RecoverySample = { pending: 0, failed: 0, oldestPendingSince: null };

/** Reactive slice, kept small and quantized so subscribers re-render only on meaningful change. */
export interface FreshnessState {
  readonly phase: FreshnessPhase;
  readonly cableConnected: boolean;
  readonly networkOnline: boolean;
  readonly pendingRecoveries: number;
  readonly failedRecoveries: number;
  readonly feedError: string | null;
  /** Whole seconds the oldest outstanding recovery has been open; null when nothing is outstanding. */
  readonly recoveryLagSeconds: number | null;
  /** When the current phase was entered. */
  readonly phaseSince: number;
}

/** Full detail for logging and support, including values too churny for the reactive snapshot. */
export interface FreshnessReport extends FreshnessState {
  readonly msSinceLastLiveEvent: number | null;
  readonly msSinceLastReady: number | null;
  readonly at: number;
}

interface EventTargetLike {
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

interface Scheduler {
  setInterval(handler: () => void, timeout: number): ReturnType<typeof setInterval>;
  clearInterval(handle: ReturnType<typeof setInterval>): void;
}

/** Injectable sources; production wiring lives in connectionFreshnessBootstrap. */
export interface FreshnessTrackerOptions {
  readonly eventTarget: EventTargetLike;
  readonly initialCableConnected?: boolean;
  readonly initialNetworkOnline?: boolean;
  readonly now?: () => number;
  readonly scheduler?: Scheduler;
  /** Pushes recovery samples; returns an unsubscribe. Omit when nothing owns recovery yet. */
  readonly observeRecoveries?: (cb: (sample: RecoverySample) => void) => () => void;
  readonly onTransition?: (next: FreshnessReport, previous: FreshnessPhase) => void;
}

const INITIAL_STATE: FreshnessState = {
  phase: 'connecting',
  cableConnected: false,
  networkOnline: true,
  pendingRecoveries: 0,
  failedRecoveries: 0,
  feedError: null,
  recoveryLagSeconds: null,
  phaseSince: 0
};

/**
 * Derives one answer to "is this client caught up?" from the signals already on the page: cable
 * lifecycle, network lifecycle, outstanding recovery work, and the feed's own reconciliation
 * result. Deliberately observational — it reports on recovery rather than performing any.
 */
export class ConnectionFreshnessTracker {
  private readonly eventTarget: EventTargetLike;
  private readonly now: () => number;
  private readonly scheduler: Scheduler;
  private readonly observeRecoveries: ((cb: (sample: RecoverySample) => void) => () => void) | undefined;
  private readonly onTransition: ((next: FreshnessReport, previous: FreshnessPhase) => void) | undefined;
  private readonly subscribers = new Set<(state: FreshnessState) => void>();

  private state: FreshnessState = INITIAL_STATE;
  private recovery: RecoverySample = EMPTY_RECOVERY;
  private feedError: string | null = null;
  private cableConnected = false;
  private networkOnline = true;
  private lastLiveEventAt: number | null = null;
  private lastReadyAt: number | null = null;
  private unobserveRecoveries: (() => void) | undefined;
  private tickHandle: ReturnType<typeof setInterval> | undefined;
  private started = false;

  constructor(options: FreshnessTrackerOptions) {
    this.eventTarget = options.eventTarget;
    this.now = options.now ?? Date.now;
    // Wrapped rather than referenced: calling a bare `setInterval` through an object literal loses
    // the window receiver, and browsers throw "Illegal invocation" on unbound timer natives.
    this.scheduler = options.scheduler ?? {
      setInterval: (handler, timeout) => globalThis.setInterval(handler, timeout),
      clearInterval: (handle) => globalThis.clearInterval(handle)
    };
    this.observeRecoveries = options.observeRecoveries;
    this.onTransition = options.onTransition;
    this.cableConnected = options.initialCableConnected ?? false;
    this.networkOnline = options.initialNetworkOnline ?? true;
    this.state = {
      ...INITIAL_STATE,
      cableConnected: this.cableConnected,
      networkOnline: this.networkOnline,
      phaseSince: this.now()
    };
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.eventTarget.addEventListener(CABLE_EVENT, this.handleCableEvent);
    this.eventTarget.addEventListener(NETWORK_EVENT, this.handleNetworkEvent);
    this.eventTarget.addEventListener(LIVE_MESSAGE_EVENT, this.handleLiveEvent);
    this.unobserveRecoveries = this.observeRecoveries?.((sample) => {
      this.recovery = sample;
      this.recompute();
    });
    this.recompute();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    this.eventTarget.removeEventListener(CABLE_EVENT, this.handleCableEvent);
    this.eventTarget.removeEventListener(NETWORK_EVENT, this.handleNetworkEvent);
    this.eventTarget.removeEventListener(LIVE_MESSAGE_EVENT, this.handleLiveEvent);
    this.unobserveRecoveries?.();
    this.unobserveRecoveries = undefined;
    this.stopTicking();
  }

  getSnapshot(): FreshnessState {
    return this.state;
  }

  /** Subscribes without an immediate call, matching useSyncExternalStore's contract. */
  subscribe(cb: (state: FreshnessState) => void): () => void {
    this.subscribers.add(cb);
    return () => {
      this.subscribers.delete(cb);
    };
  }

  /**
   * Reports the active feed's reconciliation result. The feed repairs its own channel on a durable
   * cursor, so its terminal failure is the one recovery signal the envelope table cannot see.
   */
  reportFeedFreshness(error: string | null | undefined): void {
    const next = error ?? null;
    if (this.feedError === next) return;
    this.feedError = next;
    this.recompute();
  }

  /** Point-in-time detail for logs, Sentry breadcrumbs, and support tooling. */
  describe(): FreshnessReport {
    const at = this.now();
    return {
      ...this.state,
      msSinceLastLiveEvent: this.lastLiveEventAt === null ? null : at - this.lastLiveEventAt,
      msSinceLastReady: this.lastReadyAt === null ? null : at - this.lastReadyAt,
      at
    };
  }

  private readonly handleCableEvent: EventListener = (event) => {
    const connected = booleanField(eventDetail(event), 'connected');
    if (connected === undefined) return;
    this.cableConnected = connected;
    this.recompute();
  };

  private readonly handleNetworkEvent: EventListener = (event) => {
    const detail = eventDetail(event);
    // Network events carry a nested `network` object; a cable event never does, so the two
    // sources stay distinguishable rather than collapsing into one ambiguous connected flag.
    const network = isRecord(detail) && isRecord(detail.network) ? detail.network : undefined;
    const online = network === undefined ? booleanField(detail, 'online') : booleanField(network, 'online');
    if (online === undefined) return;
    this.networkOnline = online;
    this.recompute();
  };

  private readonly handleLiveEvent: EventListener = () => {
    this.lastLiveEventAt = this.now();
  };

  private recompute(): void {
    const at = this.now();
    const phase = this.computePhase();
    const previous = this.state.phase;
    if (phase === 'ready' && previous !== 'ready') this.lastReadyAt = at;

    const next: FreshnessState = {
      phase,
      cableConnected: this.cableConnected,
      networkOnline: this.networkOnline,
      pendingRecoveries: this.recovery.pending,
      failedRecoveries: this.recovery.failed,
      feedError: this.feedError,
      recoveryLagSeconds: this.recoveryLagSeconds(at),
      phaseSince: phase === previous ? this.state.phaseSince : at
    };

    this.syncTicking(next);
    if (isShallowEqual(this.state, next)) return;
    this.state = next;

    if (phase !== previous) this.onTransition?.(this.describe(), previous);
    for (const subscriber of this.subscribers) subscriber(next);
  }

  private computePhase(): FreshnessPhase {
    if (!this.networkOnline) return 'offline';
    if (!this.cableConnected) return 'connecting';
    // Connected and knowingly behind: the state the transport-only indicator could never show.
    if (this.recovery.failed > 0 || this.feedError !== null) return 'stale';
    if (this.recovery.pending > 0) return 'resuming';
    return 'ready';
  }

  private recoveryLagSeconds(at: number): number | null {
    const since = this.recovery.oldestPendingSince;
    if (since === null) return null;
    return Math.max(0, Math.floor((at - since) / 1000));
  }

  private syncTicking(next: FreshnessState): void {
    const wanted = this.started && next.recoveryLagSeconds !== null;
    if (wanted && this.tickHandle === undefined) {
      this.tickHandle = this.scheduler.setInterval(() => this.recompute(), LAG_TICK_MS);
      return;
    }
    if (!wanted) this.stopTicking();
  }

  private stopTicking(): void {
    if (this.tickHandle === undefined) return;
    this.scheduler.clearInterval(this.tickHandle);
    this.tickHandle = undefined;
  }
}

function eventDetail(event: Event): unknown {
  return 'detail' in event ? (event as Event & { detail: unknown }).detail : undefined;
}

function booleanField(value: unknown, key: string): boolean | undefined {
  if (!isRecord(value)) return undefined;
  return typeof value[key] === 'boolean' ? value[key] : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isShallowEqual(a: FreshnessState, b: FreshnessState): boolean {
  return (Object.keys(a) as (keyof FreshnessState)[]).every((key) => a[key] === b[key]);
}
